import {
  Button,
  Checkbox,
  Empty,
  Flex,
  Input,
  Modal,
  Skeleton,
  Typography
} from "antd"
import { useCallback, useEffect, useMemo, useState } from "react"

import { useAppDispatch } from "../../../../../../../hooks/use-app-dispatch.ts"
import { getAttachments } from "../../../../../../Album/redux/attachments.thunks.ts"
import type { Attachment } from "../../../../../../Album/types/attachment.types.ts"
import { AttachmentSelectorCard } from "./AttachmentSelectorCard.tsx"

const { Text } = Typography
const { Search } = Input

interface AttachmentSelectorProps {
  projectId: string
  existingAttachmentIds: string[]
  onClose: () => void
  onSubmit: (attachmentIds: string[]) => Promise<void>
}

export function AttachmentSelector({
  projectId,
  existingAttachmentIds,
  onClose,
  onSubmit
}: AttachmentSelectorProps) {
  const dispatch = useAppDispatch()

  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState("")

  const fetchAttachments = useCallback(async () => {
    try {
      setLoading(true)
      const result = await dispatch(
        getAttachments({ project: projectId })
      ).unwrap()
      setAttachments(result || [])
    } catch (e) {
      console.error("Failed to fetch attachments:", e)
    } finally {
      setLoading(false)
    }
  }, [dispatch, projectId])

  useEffect(() => {
    void fetchAttachments()
  }, [fetchAttachments])

  const availableAttachments = useMemo(() => {
    return attachments.filter(
      (attachment) => !existingAttachmentIds.includes(attachment.id)
    )
  }, [attachments, existingAttachmentIds])

  const filteredAttachments = useMemo(() => {
    if (!searchQuery) return availableAttachments

    const query = searchQuery.toLowerCase()
    return availableAttachments.filter((attachment) =>
      attachment.original_filename.toLowerCase().includes(query)
    )
  }, [availableAttachments, searchQuery])

  const handleToggleAttachment = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const handleToggleAll = () => {
    if (selectedIds.length === filteredAttachments.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(filteredAttachments.map((a) => a.id))
    }
  }

  const handleSubmit = async () => {
    if (selectedIds.length === 0) return

    try {
      setSubmitting(true)
      await onSubmit(selectedIds)
      onClose()
    } catch (e) {
      console.error("Failed to add attachments:", e)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="Add Files to Album"
      open
      closable={true}
      maskClosable={false}
      keyboard={false}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={`Add ${selectedIds.length} file(s)`}
      cancelText="Cancel"
      confirmLoading={submitting}
      okButtonProps={{ disabled: selectedIds.length === 0 }}
      width={900}
      styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
    >
      <Flex vertical gap={16}>
        <Flex gap={8} align="center">
          <Search
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            className="flex-1"
          />
          <Checkbox
            checked={
              filteredAttachments.length > 0 &&
              selectedIds.length === filteredAttachments.length
            }
            indeterminate={
              selectedIds.length > 0 &&
              selectedIds.length < filteredAttachments.length
            }
            onChange={handleToggleAll}
          >
            Select all ({filteredAttachments.length})
          </Checkbox>
        </Flex>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton.Node key={i} className={"w-full h-[300px]"} />
            ))}
          </div>
        ) : filteredAttachments.length === 0 ? (
          <Empty
            description={
              availableAttachments.length === 0
                ? "No available files to add"
                : "No files match your search"
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredAttachments.map((attachment) => (
              <AttachmentSelectorCard
                key={attachment?.id}
                attachment={attachment}
                onToggleAttachment={handleToggleAttachment}
                isSelected={selectedIds.includes(attachment?.id)}
              />
            ))}
          </div>
        )}

        {selectedIds.length > 0 && (
          <Flex
            justify="space-between"
            align="center"
            className="bg-blue-50 p-3 rounded"
          >
            <Text strong className="text-blue-700">
              {selectedIds.length} file(s) selected
            </Text>
            <Button type="link" size="small" onClick={() => setSelectedIds([])}>
              Clear selection
            </Button>
          </Flex>
        )}
      </Flex>
    </Modal>
  )
}
